import type { PartialDeep } from 'type-fest'
import type { Path } from 'vee-validate'
import type { ComputedRef, Ref } from 'vue'
import type { ZodRawShape, ZodTypeAny } from 'zod'
import type { FormFieldType } from '../../../const/formField'

import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { computed } from 'vue'
import { object } from 'zod'

import { pureValidator, requiredValidator, validateObject } from './validator'

export interface SchemaType<K extends string = string> {
  group?: string
  type: FormFieldType
  name: K
  metaName?: string
  label: string
  labelIsVisible?: boolean
  labelIcon?: { name: string, onClick: () => void }
  attrs?: Record<string, unknown>
  handlers?: Record<string, unknown>
  required?: boolean
  validation?: ZodTypeAny
  layout?: { xs?: number, sm?: number, md?: number, lg?: number, xl?: number }
  hidden?: Array<'xs' | 'sm' | 'md' | 'lg' | 'xl'>
}

export type HandleSubmitType<T extends Record<string, unknown>> = (
  callback: (data: T) => void,
) => (e?: Event) => Promise<void | undefined | T>

export interface ValidationType<T extends Record<string, unknown>> {
  handleSubmit: HandleSubmitType<T>
  fields: { [K in keyof T]: Ref<T[K]> }
  validate: () => Promise<{
    valid: boolean
    errors: Partial<Record<string, string | undefined>>
  }>
  isValid: ComputedRef<boolean>
  isDirty: ComputedRef<boolean>
  errors: ComputedRef<Partial<Record<string, string | undefined>>>
}

export function useValidation<
  T extends Record<string, unknown>,
  K extends keyof T & string = keyof T & string,
  V = T[K],
>(
  schema: SchemaType<K>[],
  defaultValue?: Record<K, V>,
  customValidator?: Record<FormFieldType, ZodTypeAny>,
): ValidationType<Record<K, V>> {
  const usedValidator = customValidator || requiredValidator
  const validationSchema = toTypedSchema(
    object(
      schema.reduce((acc, item) => {
        if (!item.required) {
          acc[item.name] = item.validation ? item.validation : pureValidator[item.type].optional()
          return acc
        }

        acc[item.name] = item.validation
          ? item.validation
          : usedValidator[item.type]
        return acc
      }, {} as ZodRawShape),
    ).superRefine(validateObject),
  )

  const { handleSubmit, defineField, meta, errors, validate } = useForm<
    Record<K, V>
  >({
    validationSchema,
    validateOnMount: false,
    ...(defaultValue
      ? { initialValues: defaultValue as PartialDeep<Record<K, V>> }
      : {}),
  })

  const fields = schema.reduce(
    (acc, data) => {
      acc[data.name] = defineField(
        data.name as unknown as Path<Record<K, V>>,
      )[0] as Ref<V>
      return acc
    },
    {} as Record<K, Ref<V>>,
  )

  return {
    handleSubmit,
    fields,
    validate,
    isValid: computed(() => meta.value.valid),
    isDirty: computed(() => meta.value.dirty),
    errors,
  }
}
